import { useEffect, useRef, useState, forwardRef, ReactElement, cloneElement } from 'react'
import ReactDOM from 'react-dom'

import useKeylistener from 'hooks/useKeylistener'

import { DropdownProps, Option } from 'components/elements/ArchidektDropdown/types'
import { CardType } from 'types/deck'
import { LocalCardData } from 'components/card/deckCards/deckCardWrapper'

import DrawerMenu from 'components/elements/DrawerMenu'
import Menu from 'components/elements/ArchidektDropdown/Menu'
import DrawerTopper from 'components/card/DrawerTopper'

import styles from './contextMenu.module.scss'

const BODY_CLASS = 'context-menu-open'

type Props = {
  id?: string
  checkSkipRightClick?: () => boolean
  className?: string
  style?: React.CSSProperties
  onMouseDown?: (e: React.MouseEvent) => void
  onMouseMove?: (e: React.MouseEvent) => void
  onMouseUp?: (e: React.MouseEvent) => void
  onFocus?: (e: React.FocusEvent) => void
  disabled?: boolean
  openOnLeftClick?: boolean
  skipPinningOnLeftClickAction?: boolean
  noBrowserContextMenuWithNoOptions?: boolean
  matchMenuWidthToParent?: boolean
  customMenu?: ReactElement
  pinToLeftFirst?: boolean
  openMenuAsDrawerOnMobile?: boolean
  openMenuAsDrawerOnTouch?: boolean
  forceOpenAsDrawer?: boolean
  card?: CardType
  localCardData?: LocalCardData
} & DropdownProps

type Direction = 'top left' | 'top right' | 'bottom left' | 'bottom right' | 'center center'
export type CustomMenuProps = { onClose?: () => void }

const ContextMenu = forwardRef((props: Props, ref: any) => {
  const menuWrapperRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const touching = useRef(false)

  const [openX, setOpenX] = useState<number | null>(null)
  const [openY, setOpenY] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [pinToTrigger, setPinToTrigger] = useState(false)
  const [openDirection, setOpenDirection] = useState<Direction | null>(null)
  const [visable, setVisable] = useState(false) // used to control whether or not the context menu is visable at all (@see handleRightClick for more info)
  const [leaving, setLeaving] = useState(false)

  const dropdownProps = { ...props, children: undefined } // Remove children from dropdown props

  useKeylistener(
    e => {
      if (!openX || !openY || !visable) return

      if (e.key === 'Escape') {
        e.preventDefault() // prevents exiting fullscreen on macos (I think)

        handleCloseOverlay()
      }
    },
    [openX, openY, visable],
  )

  const handleRightClick = (e: React.MouseEvent, activatedViaLeftClick = false) => {
    const visableOptions = props.options?.filter(opt => !opt.hidden) || []

    if (props.disabled) return
    if (!visableOptions?.length && !props.customMenu) {
      if (props.noBrowserContextMenuWithNoOptions) {
        e.preventDefault()
        e.stopPropagation()
      }

      return // No options, do nothing
    }
    if (e.ctrlKey) return // use the default right click

    // @ts-ignore
    const clickedElement: Element = e.target

    if (clickedElement.id === 'contextMenuOverlay') return // If the click hits the overlay, do nothing

    if (
      clickedElement?.tagName === 'INPUT' ||
      clickedElement?.tagName === 'input' ||
      clickedElement?.tagName === 'TEXTAREA' ||
      clickedElement?.tagName === 'textarea' ||
      clickedElement?.classList.contains('editor') ||
      clickedElement?.parentElement?.classList.contains('editor')
    )
      return

    if (props.checkSkipRightClick && !props.checkSkipRightClick()) return
    if ((openX !== null && openY !== null) || drawerOpen) return handleCloseOverlay()

    e.preventDefault()
    e.stopPropagation()

    // Open as drawer
    if (
      props.forceOpenAsDrawer ||
      (props.openMenuAsDrawerOnTouch && touching.current) ||
      // Assume they're on a touch screen when on < 600 px wide regardless to cover for weird safari iSO event handler pipeline not firing in the correct order
      ((props.openMenuAsDrawerOnMobile || props.openMenuAsDrawerOnTouch) && document.documentElement.clientWidth < 600)
    ) {
      setDrawerOpen(true)
    }
    // Open as normal context menu
    else {
      setPinToTrigger(activatedViaLeftClick)

      setOpenX(e.clientX)
      setOpenY(e.clientY)
    }

    document.body.classList.add(BODY_CLASS)

    setTimeout(() => setVisable(true), 100) // Delay being able to see the context menu by 100ms so we can recalculate whether it needs to be moved for edge detection
  }

  const handleCloseOverlay = (e?: React.MouseEvent | React.TouchEvent) => {
    if (e?.target !== e?.currentTarget) return

    e?.preventDefault()
    e?.stopPropagation()

    setLeaving(true)
    setDrawerOpen(false) // Doesn't need the timeout delay for the drawer

    // If we close the overly too quickly (aka before stopPropagation finishes) the click bubbles down
    setTimeout(() => {
      setOpenX(null)
      setOpenY(null)
      setDrawerOpen(false)
      setOpenDirection(null)
      setVisable(false)
      setLeaving(false)

      if (props.openOnLeftClick) containerRef?.current?.focus()

      document.body.classList.remove(BODY_CLASS)
    }, 150)
  }

  const handleMenuItemClick = (_e: React.MouseEvent, optionOnClick?: () => void, noCloseOnClick?: boolean) => {
    if (optionOnClick) optionOnClick()
    if (noCloseOnClick) return

    handleCloseOverlay()
  }

  const handleClick = (e: React.MouseEvent) => {
    // If we don't care about opening on left click, or we're already open, do nothing
    if (!props.openOnLeftClick || !!openX || !!openY) return

    e.preventDefault()
    e.stopPropagation()

    handleRightClick(e, !props.skipPinningOnLeftClickAction)
  }

  const handlePassthroughRightClick = (e: React.MouseEvent) => {
    if (e.button === 2 && e.currentTarget !== e.target) return

    if (e.ctrlKey) return
    if (!overlayRef.current) return

    e.preventDefault()
    e.stopPropagation()

    setVisable(false)
    setOpenX(null)
    setOpenY(null)

    overlayRef.current.style.pointerEvents = 'none'

    const clickPositionX = e.clientX
    const clickPositionY = e.clientY

    const element = window.document.elementFromPoint(clickPositionX, clickPositionY)

    if (!element) return

    const arr = ['mousedown', 'contextmenu', 'mouseup']

    arr.forEach(mouseEventType =>
      element.dispatchEvent(
        new MouseEvent(mouseEventType, {
          view: window,
          bubbles: true,
          cancelable: true,
          buttons: 2,
          clientX: clickPositionX,
          clientY: clickPositionY,
        }),
      ),
    )
  }

  useEffect(() => {
    if (openY === null || openX === null) return
    if (!menuWrapperRef.current) return

    // @ts-expect-error
    const menu: HTMLDivElement = menuWrapperRef.current.childNodes[0]

    if (!menu) return

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const menuWidth = menu.clientWidth
    const menuHeight = menu.clientHeight

    const triggerPosition = containerRef?.current?.getBoundingClientRect()

    let updatedX = openX
    let updatedY = openY
    let updatedOpenDirection: Direction | null = null

    // Pin to trigger (aka - standard dropdown logic)
    if (pinToTrigger && triggerPosition) {
      updatedX = triggerPosition.right - menuWidth
      updatedY = triggerPosition.bottom + 3

      updatedOpenDirection = 'top right'

      // Allows context menus to open leftward before right
      if (props.pinToLeftFirst) {
        updatedX = triggerPosition.left
        updatedOpenDirection = 'top left'

        if (viewportWidth < updatedX) {
          updatedX = triggerPosition.right - menuWidth
          updatedOpenDirection = 'top right'
        }
      }

      if (updatedX < 0) {
        updatedX = triggerPosition.left
        updatedOpenDirection = 'top left'
      }

      if (props.matchMenuWidthToParent && containerRef.current) {
        if (menuWrapperRef.current) {
          menuWrapperRef.current.style.width = `${containerRef.current?.clientWidth}px`
          menuWrapperRef.current.style.position = 'relative'
        }

        updatedX = triggerPosition.left
        updatedOpenDirection = 'top left'
      } else {
        menuWrapperRef.current.style = ''
      }
    }

    const hitXboarder = menuWidth + updatedX > viewportWidth
    const hitYboarder = menuHeight + updatedY > viewportHeight

    // Overrides in the case where the menu is pushed offscreen
    if (hitXboarder) updatedX = viewportWidth - menuWidth - 10
    if (hitYboarder) updatedY = viewportHeight - menuHeight - 10

    setOpenX(updatedX)
    setOpenY(updatedY)

    if (updatedOpenDirection) setOpenDirection(updatedOpenDirection)
  }, [openY, openX])

  useEffect(() => {
    return () => document.body.classList.remove(BODY_CLASS)
  }, [])

  useEffect(() => {
    if (visable) menuWrapperRef.current?.focus()
  }, [visable])

  const menuLocation: React.CSSProperties = {
    top: openY || 0,
    left: openX || 0,
    display: 'unset',
    opacity: visable ? 1 : 0,
    transformOrigin: openDirection || undefined,
  }

  // Appending card name header here so we can append the drawer topper in the drawer
  const contextOptions: Option[] = [
    { type: 'header', label: props.card?.name, hidden: !props.card },
    { type: 'spacer', hidden: !props.card },
    ...(dropdownProps.options || []),
  ]

  return (
    <div
      id={props.id}
      ref={element => {
        if (ref) ref.current = element

        // @ts-ignore
        if (containerRef) containerRef.current = element
      }}
      style={props.style}
      className={`${styles.wrapper} ${props.className || ''}`}
      onContextMenu={drawerOpen ? undefined : handleRightClick}
      onClick={drawerOpen ? undefined : handleClick}
      onMouseDown={props.onMouseDown}
      onMouseMove={props.onMouseMove}
      onMouseUp={props.onMouseUp}
      onFocus={props.onFocus}
      onPointerDown={e => (touching.current = e.pointerType === 'touch')}
      // Safari ios ignores spec and onPointerUp fires _after_ onClick (SOMETIMES...)
      // In those scenarios, the touch menu won't open, and instead
      onPointerUp={() => setTimeout(() => (touching.current = false), 100)}
      tabIndex={-1}>
      {props.children}

      <DrawerMenu
        open={drawerOpen}
        customOptionsTop={
          props.customOptionsTop ??
          (props.card ? (
            <DrawerTopper
              card={props.card}
              localCardData={props.localCardData}
              includeCardText
              flipped={props.localCardData?.flipped || false}
            />
          ) : undefined)
        }
        options={props.options}
        onClose={handleCloseOverlay}
      />

      {openY !== null &&
        openX !== null &&
        ReactDOM.createPortal(
          <div
            id="contextMenuOverlay"
            ref={overlayRef}
            className={styles.overlay}
            onClick={handleCloseOverlay}
            onContextMenu={handlePassthroughRightClick}>
            <div ref={menuWrapperRef} tabIndex={-1}>
              {props.customMenu && (
                <div
                  style={menuLocation}
                  className={`${styles.menu} ${leaving ? styles.exiting : ''} ${dropdownProps.menuClassName || ''}`}>
                  {cloneElement(props.customMenu, { onClose: handleCloseOverlay })}
                </div>
              )}

              {!props.customMenu && (
                <Menu
                  {...dropdownProps}
                  options={contextOptions}
                  menuStyle={menuLocation}
                  menuClassName={`${styles.menu} ${leaving ? styles.exiting : ''} ${dropdownProps.menuClassName || ''}`}
                  unFocusClick={handleMenuItemClick}
                />
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
})

export default ContextMenu
